import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  InboxIcon,
  PaperAirplaneIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  HeartIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ShareIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Postcard, PostcardExchange, User } from '../types';
import { retryService } from '../services/retryService';

// Helper function to convert Firestore timestamp to Date
const convertToDate = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'object' && timestamp !== null && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as { toDate: () => Date }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  return new Date(String(timestamp));
};

interface InboxPostcard extends Postcard {
  exchangeId: string;
  exchangeStatus: 'sent' | 'delivered' | 'read' | 'replied' | 'failed' | 'retrying';
  senderName?: string;
  senderPhotoURL?: string;
  recipientName?: string;
  recipientPhotoURL?: string;
  isLiked: boolean;
  replyPostcard?: Postcard;
  retryCount?: number;
  maxRetries?: number;
  retryErrorMessage?: string;
}

export function InboxPage() {
  const { currentUser } = useAuth();
  const [postcards, setPostcards] = useState<InboxPostcard[]>([]);
  const [receivedCount, setReceivedCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'replied' | 'pending'>('all');
  const [selectedPostcard, setSelectedPostcard] = useState<InboxPostcard | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchAllCounts(); // Fetch counts for both tabs on initial load
    }
  }, [currentUser]);

  useEffect(() => {
    fetchPostcards();
  }, [activeTab, currentUser]);

  const fetchAllCounts = async () => {
    if (!currentUser) return;

    try {
      // Fetch received count
      const receivedExchangesQuery = query(
        collection(db, 'postcardExchanges'),
        where('recipientId', '==', currentUser.uid)
      );
      const receivedSnapshot = await getDocs(receivedExchangesQuery);
      setReceivedCount(receivedSnapshot.docs.length);

      // Fetch sent count
      const sentExchangesQuery = query(
        collection(db, 'postcardExchanges'),
        where('senderId', '==', currentUser.uid)
      );
      const sentSnapshot = await getDocs(sentExchangesQuery);
      setSentCount(sentSnapshot.docs.length);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchPostcards = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      if (activeTab === 'received') {
        await fetchReceivedPostcards();
      } else {
        await fetchSentPostcards();
      }
    } catch (error) {
      console.error('Error fetching postcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceivedPostcards = async () => {
    if (!currentUser) return;
    
    // Fetch exchanges where current user is recipient
    const exchangesQuery = query(
      collection(db, 'postcardExchanges'),
      where('recipientId', '==', currentUser.uid),
      orderBy('sentAt', 'desc')
    );
    const exchangesSnapshot = await getDocs(exchangesQuery);
    
    const receivedPostcards: InboxPostcard[] = [];
    
    for (const exchangeDoc of exchangesSnapshot.docs) {
      const exchange = exchangeDoc.data() as PostcardExchange;
      
      // Fetch the postcard
      const postcardDoc = await getDoc(doc(db, 'postcards', exchange.postcardId));
      
      if (postcardDoc.exists()) {
        const postcard = postcardDoc.data() as Postcard;
        
        // Fetch sender info
        let sender: User | null = null;
        if (exchange.senderId) {
          const senderDoc = await getDoc(doc(db, 'users', exchange.senderId));
          sender = senderDoc.exists() ? senderDoc.data() as User : null;
        }
        
        // Fetch reply postcard if exists
        let replyPostcard: Postcard | undefined;
        if (exchange.replyPostcardId) {
          const replyDoc = await getDoc(doc(db, 'postcards', exchange.replyPostcardId));
          if (replyDoc.exists()) {
            replyPostcard = replyDoc.data() as Postcard;
          }
        }
        
        receivedPostcards.push({
          ...postcard,
          id: postcardDoc.id,
          exchangeId: exchangeDoc.id,
          exchangeStatus: exchange.status,
          senderName: sender?.displayName || 'Anonymous',
          senderPhotoURL: sender?.photoURL,
          isLiked: false,
          replyPostcard
        });
      }
    }
    
    setPostcards(receivedPostcards);
    setReceivedCount(receivedPostcards.length);
  };

  const fetchSentPostcards = async () => {
    if (!currentUser) return;
    
    // Fetch exchanges where current user is sender
    const exchangesQuery = query(
      collection(db, 'postcardExchanges'),
      where('senderId', '==', currentUser.uid),
      orderBy('sentAt', 'desc')
    );
    const exchangesSnapshot = await getDocs(exchangesQuery);
    
    const sentPostcards: InboxPostcard[] = [];
    
    for (const exchangeDoc of exchangesSnapshot.docs) {
      const exchange = exchangeDoc.data() as PostcardExchange;
      
      // Fetch the postcard
      const postcardDoc = await getDoc(doc(db, 'postcards', exchange.postcardId));
      
      if (postcardDoc.exists()) {
        const postcard = postcardDoc.data() as Postcard;
        
        // Fetch recipient info if not random
        let recipient: User | null = null;
        if (exchange.recipientId && exchange.recipientId !== 'random') {
          const recipientDoc = await getDoc(doc(db, 'users', exchange.recipientId));
          recipient = recipientDoc.exists() ? recipientDoc.data() as User : null;
        }
        
        // Fetch reply postcard if exists
        let replyPostcard: Postcard | undefined;
        if (exchange.replyPostcardId) {
          const replyDoc = await getDoc(doc(db, 'postcards', exchange.replyPostcardId));
          if (replyDoc.exists()) {
            replyPostcard = replyDoc.data() as Postcard;
          }
        }
        
        sentPostcards.push({
          ...postcard,
          exchangeId: exchangeDoc.id,
          exchangeStatus: exchange.status,
          recipientName: recipient?.displayName || 'Random User',
          recipientPhotoURL: recipient?.photoURL,
          isLiked: false,
          replyPostcard
        });
      }
    }
    
    setPostcards(sentPostcards);
    setSentCount(sentPostcards.length);
  };

  const markAsRead = async (postcard: InboxPostcard) => {
    if (!currentUser) return;

    try {
      // Update exchange status
      const exchangeRef = doc(db, 'postcardExchanges', postcard.exchangeId);
      await updateDoc(exchangeRef, {
        status: 'read',
        readAt: new Date()
      });

      // Update local state
      setPostcards(prev => 
        prev.map(p => 
          p.id === postcard.id 
            ? { ...p, isRead: true, exchangeStatus: 'read' }
            : p
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const toggleLike = async (postcard: InboxPostcard) => {
    if (!currentUser) return;

    try {
      // Create or update a like document in a separate likes collection
      const likeRef = doc(db, 'postcardLikes', `${postcard.id}_${currentUser.uid}`);
      
      if (postcard.isLiked) {
        // Unlike: delete the like document
        await deleteDoc(likeRef);
        
        // Update local state
        setPostcards(prev => 
          prev.map(p => 
            p.id === postcard.id 
              ? { ...p, likes: Math.max(0, (p.likes || 0) - 1), isLiked: false }
              : p
          )
        );
      } else {
        // Like: create the like document
        await setDoc(likeRef, {
          postcardId: postcard.id,
          userId: currentUser.uid,
          createdAt: serverTimestamp()
        });
        
        // Update local state
        setPostcards(prev => 
          prev.map(p => 
            p.id === postcard.id 
              ? { ...p, likes: (p.likes || 0) + 1, isLiked: true }
              : p
          )
        );
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const replyToPostcard = async (originalPostcard: InboxPostcard) => {
    // Navigate to create postcard page with reply context
    // This would be handled by the router
    window.location.href = `/create?replyTo=${originalPostcard.id}`;
  };

  const sharePostcard = async (postcard: InboxPostcard) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Check out this postcard from ${postcard.senderName || 'Anonymous'}`,
          text: postcard.message,
          url: `${window.location.origin}/postcard/${postcard.id}`
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(`Check out this amazing postcard: ${postcard.message} - ${window.location.origin}/postcard/${postcard.id}`);
      alert('Link copied to clipboard!');
    }
  };

  const retryFailedPostcard = async (postcard: InboxPostcard) => {
    if (!currentUser) return;

    try {
      // Update exchange status to retrying
      const exchangeRef = doc(db, 'postcardExchanges', postcard.exchangeId);
      await updateDoc(exchangeRef, {
        status: 'retrying',
        retryCount: (postcard.retryCount || 0) + 1,
        lastRetryAttempt: new Date()
      });

      // Update postcard status
      const postcardRef = doc(db, 'postcards', postcard.id);
      await updateDoc(postcardRef, {
        retryStatus: 'retrying',
        retryCount: (postcard.retryCount || 0) + 1,
        lastRetryAttempt: new Date()
      });

      // Update local state
      setPostcards(prev => 
        prev.map(p => 
          p.id === postcard.id 
            ? { 
                ...p, 
                exchangeStatus: 'retrying',
                retryCount: (p.retryCount || 0) + 1,
                lastRetryAttempt: new Date()
              }
            : p
        )
      );

      // Add to background retry queue
      retryService.addToRetryQueue({
        postcardId: postcard.id,
        exchangeId: postcard.exchangeId,
        maxRetries: postcard.maxRetries || 5,
        onSuccess: (result) => {
          console.log(`Background retry succeeded for postcard ${postcard.id}:`, result);
          // Update local state to show success
          setPostcards(prev => 
            prev.map(p => 
              p.id === postcard.id 
                ? { 
                    ...p, 
                    exchangeStatus: 'sent',
                    retryStatus: 'success',
                    recipientId: result.recipientId,
                    recipientName: result.recipientName
                  }
                : p
            )
          );
        },
        onError: (error) => {
          console.error(`Background retry failed for postcard ${postcard.id}:`, error);
          // Update local state to show failure
          setPostcards(prev => 
            prev.map(p => 
              p.id === postcard.id 
                ? { 
                    ...p, 
                    exchangeStatus: 'failed',
                    retryStatus: 'failed',
                    retryErrorMessage: error.message
                  }
                : p
            )
          );
        },
        onComplete: (result) => {
          console.log(`Background retry completed for postcard ${postcard.id}:`, result);
        }
      });

      console.log('Retry initiated for postcard:', postcard.id);
    } catch (error) {
      console.error('Error retrying postcard:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <PaperAirplaneIcon className="h-4 w-4 text-blue-500" />;
      case 'delivered':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'read':
        return <EyeIcon className="h-4 w-4 text-purple-500" />;
      case 'replied':
        return <ArrowUturnLeftIcon className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'retrying':
        return <ArrowPathIcon className="h-4 w-4 text-yellow-500 animate-spin" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'read':
        return 'Read';
      case 'replied':
        return 'Replied';
      case 'failed':
        return 'Failed';
      case 'retrying':
        return 'Retrying';
      default:
        return 'Pending';
    }
  };

  const filteredPostcards = postcards.filter(postcard => {
    // Apply filter
    if (filter === 'unread' && postcard.exchangeStatus === 'read') return false;
    if (filter === 'replied' && postcard.exchangeStatus !== 'replied') return false;
    if (filter === 'pending' && postcard.exchangeStatus !== 'sent') return false;
    
    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        postcard.message.toLowerCase().includes(searchLower) ||
        postcard.location.toLowerCase().includes(searchLower) ||
        postcard.country.toLowerCase().includes(searchLower) ||
        (postcard.senderName && postcard.senderName.toLowerCase().includes(searchLower)) ||
        (postcard.recipientName && postcard.recipientName.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">Inbox</h1>
          <p className="text-green-100">
            {activeTab === 'received' ? 'Your received postcards' : 'Your sent postcards'}
          </p>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('received')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'received'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <InboxIcon className="h-4 w-4 mr-2" />
            Received ({receivedCount})
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'sent'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
            Sent ({sentCount})
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search postcards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All</option>
              {activeTab === 'received' && <option value="unread">Unread</option>}
              <option value="replied">Replied</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Postcards Grid */}
      <div className="card">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : filteredPostcards.length === 0 ? (
          <div className="text-center py-12">
            <InboxIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab} postcards
            </h3>
            <p className="text-gray-600">
              {activeTab === 'received' 
                ? 'You haven\'t received any postcards yet.' 
                : 'You haven\'t sent any postcards yet.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPostcards.map((postcard, index) => (
              <motion.div
                key={postcard.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer ${
                  !postcard.isRead && activeTab === 'received' ? 'ring-2 ring-green-500' : ''
                }`}
                onClick={() => {
                  setSelectedPostcard(postcard);
                  setShowDetailModal(true);
                  if (!postcard.isRead && activeTab === 'received') {
                    markAsRead(postcard);
                  }
                }}
              >
                <div className="relative">
                  <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
                    <img
                      src={postcard.imageURL}
                      alt="Postcard"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2 flex items-center space-x-1 bg-white bg-opacity-90 px-2 py-1 rounded-full text-xs">
                    {getStatusIcon(postcard.exchangeStatus)}
                    <span className="font-medium">{getStatusText(postcard.exchangeStatus)}</span>
                  </div>

                  {/* Failed/Retrying Badge */}
                  {(postcard.exchangeStatus === 'failed' || postcard.exchangeStatus === 'retrying') && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      {postcard.exchangeStatus === 'failed' ? 'Failed' : 'Retrying'}
                    </div>
                  )}

                  {/* Unread Badge */}
                  {postcard.exchangeStatus !== 'read' && activeTab === 'received' && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      New
                    </div>
                  )}

                  {/* Reply Badge */}
                  {postcard.exchangeStatus === 'replied' && (
                    <div className="absolute bottom-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Replied
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <img
                      src={activeTab === 'received' ? postcard.senderPhotoURL || '/default-avatar.png' : postcard.recipientPhotoURL || '/default-avatar.png'}
                      alt={activeTab === 'received' ? postcard.senderName : postcard.recipientName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        {activeTab === 'received' ? postcard.senderName : postcard.recipientName}
                      </h3>
                      <p className="text-xs text-gray-600 flex items-center">
                        <MapPinIcon className="h-3 w-3 mr-1" />
                        {postcard.location}, {postcard.country}
                      </p>
                    </div>
                  </div>

                  {/* Message removed from list; shown on flip in detail */}

                  {/* Retry Button for Failed Postcards */}
                  {activeTab === 'sent' && postcard.exchangeStatus === 'failed' && (
                    <div className="mb-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          retryFailedPostcard(postcard);
                        }}
                        className="w-full bg-red-500 text-white py-2 px-3 rounded-md text-xs font-medium hover:bg-red-600 transition-colors"
                      >
                        <ArrowPathIcon className="h-3 w-3 inline mr-1" />
                        Retry Sending
                      </button>
                      {postcard.retryErrorMessage && (
                        <p className="text-xs text-red-600 mt-1">{postcard.retryErrorMessage}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{convertToDate(postcard.createdAt)?.toLocaleDateString()}</span>
                    <div className="flex items-center space-x-2">
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {postcard.likes}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
          {showDetailModal && selectedPostcard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {activeTab === 'received' ? 'Received Postcard' : 'Sent Postcard'}
                </h2>
                <button
                      onClick={() => { setShowDetailModal(false); setIsFlipped(false); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

                  <div className="space-y-4">
                    {/* Envelope open animation + flip card */}
                    <div className="w-full flex justify-center">
                      <div className="w-[320px] sm:w-[360px] md:w-[400px]">
                        <div className="relative [perspective:1000px]">
                          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
                            <div className={`relative h-[400px] sm:h-[450px] md:h-[500px] [transform-style:preserve-3d] transition-transform duration-500 ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`} onClick={() => setIsFlipped(!isFlipped)}>
                              {/* Front */}
                              <div className="absolute inset-0 [backface-visibility:hidden] bg-white rounded-lg shadow-xl overflow-hidden">
                                <div className="relative w-full h-full">
                                  <div className="absolute inset-0" style={{ aspectRatio: '4 / 5' }}>
                                    <img src={selectedPostcard.imageURL} alt="Postcard" className="w-full h-full object-cover" />
                                  </div>
                                </div>
                              </div>
                              {/* Back */}
                              <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white rounded-lg shadow-xl p-4 flex flex-col justify-between">
                                <div>
                                  <div className="text-sm text-gray-500">{convertToDate(selectedPostcard.createdAt)?.toLocaleDateString()}</div>
                                  <div className="mt-2 text-gray-800 whitespace-pre-wrap">{selectedPostcard.message}</div>
                                </div>
                                <div className="text-sm text-gray-600 flex items-center justify-between">
                                  <span>{selectedPostcard.location}{selectedPostcard.country ? `, ${selectedPostcard.country}` : ''}</span>
                                  <span>Tap to flip</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                  <img
                    src={activeTab === 'received' ? selectedPostcard.senderPhotoURL || '/default-avatar.png' : selectedPostcard.recipientPhotoURL || '/default-avatar.png'}
                    alt={activeTab === 'received' ? selectedPostcard.senderName : selectedPostcard.recipientName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {activeTab === 'received' ? selectedPostcard.senderName : selectedPostcard.recipientName}
                    </h3>
                    <p className="text-sm text-gray-600 flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      {selectedPostcard.location}, {selectedPostcard.country}
                    </p>
                  </div>
                </div>

                    {/* Message removed here; shown on flip back */}

                <div className="flex flex-wrap gap-2">
                  {selectedPostcard.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>{convertToDate(selectedPostcard.createdAt)?.toLocaleDateString()}</span>
                    <span className="flex items-center">
                      <HeartIcon className="h-4 w-4 mr-1" />
                      {selectedPostcard.likes}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleLike(selectedPostcard)}
                      className={`p-2 rounded-full transition-colors ${
                        selectedPostcard.isLiked 
                          ? 'text-red-500 hover:text-red-600' 
                          : 'text-gray-400 hover:text-red-500'
                      }`}
                    >
                      {selectedPostcard.isLiked ? (
                        <HeartIconSolid className="h-5 w-5" />
                      ) : (
                        <HeartIcon className="h-5 w-5" />
                      )}
                    </button>
                    
                    {activeTab === 'received' && (
                      <button
                        onClick={() => replyToPostcard(selectedPostcard)}
                        className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4 mr-1" />
                        Reply
                      </button>
                    )}
                    
                    <button
                      onClick={() => sharePostcard(selectedPostcard)}
                      className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      <ShareIcon className="h-4 w-4 mr-1" />
                      Share
                    </button>
                  </div>
                </div>

                {/* Reply Postcard Display */}
                {selectedPostcard.replyPostcard && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Reply from {selectedPostcard.recipientName}</h4>
                    <div className="flex space-x-4">
                      <img
                        src={selectedPostcard.replyPostcard.imageURL}
                        alt="Reply postcard"
                        className="w-24 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 line-clamp-2">
                          {selectedPostcard.replyPostcard.message}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {convertToDate(selectedPostcard.replyPostcard.createdAt)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 